import { createElement, type CSSProperties, type ReactNode } from 'react';

export const renderRecipeContent = (content: string): ReactNode[] => {
  const parsedContent = new DOMParser().parseFromString(content, 'text/html');
  const allowedTags = new Set([
    'A', 'B', 'BLOCKQUOTE', 'BR', 'EM', 'H1', 'H2', 'H3', 'I', 'IMG', 'LI', 'OL',
    'P', 'S', 'SPAN', 'STRONG', 'U', 'UL',
  ]);
  const ignoredTags = new Set(['IFRAME', 'OBJECT', 'SCRIPT', 'STYLE', 'SVG']);

  const renderNode = (node: Node, key: string): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const element = node as HTMLElement;
    const tagName = element.tagName;
    if (ignoredTags.has(tagName)) return null;

    const children = Array.from(element.childNodes).map((child, index) =>
      renderNode(child, `${key}-${index}`)
    );
    if (!allowedTags.has(tagName)) return children;

    const style: CSSProperties = {};
    const color = element.style.color;
    const backgroundColor = element.style.backgroundColor;
    const fontFamily = element.style.fontFamily.replace(/["']/g, '').toLowerCase();
    const fontSize = element.style.fontSize.toLowerCase();
    const textAlign = element.style.textAlign.toLowerCase();

    if (/^(#[\da-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\)|[a-z]+)$/i.test(color)) {
      style.color = color;
    }
    if (/^(#[\da-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%deg]+\)|[a-z]+)$/i.test(backgroundColor)) {
      style.backgroundColor = backgroundColor;
    }
    if (['serif', 'sans-serif', 'monospace'].includes(fontFamily)) {
      style.fontFamily = fontFamily;
    }
    if (['10px', 'small', '13px', 'large', '18px', '32px', 'huge'].includes(fontSize)) {
      style.fontSize = fontSize;
    }
    if (['center', 'right', 'justify'].includes(textAlign)) {
      style.textAlign = textAlign as CSSProperties['textAlign'];
    }

    const classes = element.className.split(/\s+/);
    const alignClass = classes.find((className) =>
      ['ql-align-center', 'ql-align-right', 'ql-align-justify'].includes(className)
    );
    if (alignClass) {
      style.textAlign = alignClass.replace('ql-align-', '') as CSSProperties['textAlign'];
    }
    const indentClass = classes.find((className) => /^ql-indent-[1-8]$/.test(className));
    if (indentClass) {
      style.marginLeft = `${Number(indentClass.replace('ql-indent-', '')) * 3}em`;
    }
    if (classes.includes('ql-font-serif')) style.fontFamily = 'serif';
    if (classes.includes('ql-font-monospace')) style.fontFamily = 'monospace';
    if (classes.includes('ql-size-small')) style.fontSize = 'small';
    if (classes.includes('ql-size-large')) style.fontSize = 'large';
    if (classes.includes('ql-size-huge')) style.fontSize = '32px';

    const props: {
      key: string;
      style?: CSSProperties;
      href?: string;
      rel?: string;
      src?: string;
      alt?: string;
      loading?: 'lazy' | 'eager';
    } = { key };
    if (Object.keys(style).length > 0) props.style = style;
    if (tagName === 'A') {
      const href = element.getAttribute('href')?.trim();
      if (href && /^(https?:|mailto:|tel:)/i.test(href)) {
        props.href = href;
        props.rel = 'noreferrer';
      }
    }
    if (tagName === 'IMG') {
      const imgSrc = element.getAttribute('src')?.trim();
      if (imgSrc) {
        props.src = imgSrc;
        props.alt = element.getAttribute('alt') || 'Hình ảnh món ăn';
        props.loading = 'lazy';
        props.style = { ...props.style, display: 'block', maxWidth: '100%', height: 'auto', margin: '12px auto' };
      }
    }

    const isBulletList = tagName === 'OL' &&
      Array.from(element.children).some((child) =>
        child.tagName === 'LI' && child.getAttribute('data-list') === 'bullet'
      );
    return createElement(isBulletList ? 'ul' : tagName.toLowerCase(), props, ...children);
  };

  return Array.from(parsedContent.body.childNodes).map((node, index) =>
    renderNode(node, `recipe-${index}`)
  );
};

export const hasRecipeContent = (content: string | null | undefined): boolean =>
  Boolean(
    content &&
    new DOMParser().parseFromString(content, 'text/html').body.textContent?.trim()
  );
